/**
 * UTILIDADES PARA VALIDACIÓN DE IMÁGENES DE BANNER
 *
 * RESTRICCIONES IMPLEMENTADAS:
 * 1. Formato: Solo JPG, PNG, WebP
 * 2. Tamaño: Máximo 2MB (compresión automática si excede)
 * 3. Ratio de aspecto: 2:1 (±10% tolerancia = 1.8:1 a 2.2:1)
 * @returns true si el formato es válido
 */
export const validateImageFormat = (file: File): boolean => {
  const allowedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  return allowedFormats.includes(file.type);
};

/**
 * Valida que el tamaño del archivo no exceda el límite
 * @param file - Archivo a validar
 * @param maxSizeMB - Tamaño máximo en MB (default: 2MB)
 * @returns true si el tamaño es válido
 */
export const validateImageSize = (
  file: File,
  maxSizeMB: number = 2,
): boolean => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

/**
 * Valida las dimensiones y ratio de aspecto de una imagen
 * @param file - Archivo de imagen a validar
 * @returns Promise con resultado de validación y dimensiones
 */
export const validateImageDimensions = (
  file: File,
): Promise<{
  isValid: boolean;
  width: number;
  height: number;
  ratio: number;
  message: string;
}> => {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      const width = img.width;
      const height = img.height;
      const ratio = width / height;

      // Validar ratio de aspecto 2:1 (±10% tolerancia)
      const TARGET_RATIO = 2;
      const TOLERANCE = 0.2; // 10% de tolerancia
      const MIN_RATIO = TARGET_RATIO - TOLERANCE; // 1.8
      const MAX_RATIO = TARGET_RATIO + TOLERANCE; // 2.2

      if (ratio < MIN_RATIO || ratio > MAX_RATIO) {
        resolve({
          isValid: false,
          width,
          height,
          ratio,
          message: `La imagen debe tener una relación de aspecto cercana a 2:1. Actual: ${ratio.toFixed(2)}:1`,
        });
        return;
      }

      resolve({
        isValid: true,
        width,
        height,
        ratio,
        message: 'Imagen válida',
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({
        isValid: false,
        width: 0,
        height: 0,
        ratio: 0,
        message: 'Error al cargar la imagen',
      });
    };

    img.src = url;
  });
};

/**
 * Comprime una imagen manteniendo las dimensiones originales
 * @param file - Archivo de imagen a comprimir
 * @param quality - Calidad de compresión (0-1, default: 0.85)
 * @returns Promise con el archivo comprimido
 */
export const compressBannerImage = async (
  file: File,
  quality: number = 0.85,
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('No se pudo crear el contexto del canvas'));
          return;
        }

        // Mantener dimensiones originales
        canvas.width = img.width;
        canvas.height = img.height;

        // Dibujar imagen sin modificar relación de aspecto
        ctx.drawImage(img, 0, 0, img.width, img.height);

        // Convertir a blob con compresión
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Error al comprimir la imagen'));
              return;
            }

            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });

            resolve(compressedFile);
          },
          'image/jpeg',
          quality,
        );
      };

      img.onerror = () => reject(new Error('Error al cargar la imagen'));
    };

    reader.onerror = () => reject(new Error('Error al leer el archivo'));
  });
};

/**
 * Valida completamente una imagen de banner
 * @param file - Archivo a validar
 * @returns Promise con resultado de validación y archivo procesado
 */
export const validateBannerImage = async (
  file: File,
): Promise<{
  isValid: boolean;
  processedFile?: File;
  errors: string[];
}> => {
  const errors: string[] = [];

  // 1. Validar formato
  if (!validateImageFormat(file)) {
    errors.push('Formato no permitido. Use JPG, PNG o WebP');
  }

  // 2. Validar dimensiones y ratio
  const dimensionValidation = await validateImageDimensions(file);

  if (!dimensionValidation.isValid) {
    errors.push(dimensionValidation.message);
  }

  // Si hay errores de formato o dimensiones, retornar
  if (errors.length > 0) {
    return { isValid: false, errors };
  }

  // 3. Comprimir si excede el tamaño
  let processedFile = file;
  if (!validateImageSize(file, 2)) {
    try {
      processedFile = await compressBannerImage(file, 0.85);
    } catch (error) {
      errors.push('Error al comprimir la imagen');
      return { isValid: false, errors };
    }
  }

  return {
    isValid: true,
    processedFile,
    errors: [],
  };
};
